import { useState, useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info, Loader2 } from 'lucide-react'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface NotificationProps {
  id: string
  type: NotificationType
  title: string
  message?: string
  duration?: number
  onClose: (id: string) => void
}

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertCircle,
  info: Info,
}

const colors = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-500',
    title: 'text-green-800',
    message: 'text-green-600',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    title: 'text-red-800',
    message: 'text-red-600',
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-500',
    title: 'text-yellow-800',
    message: 'text-yellow-600',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    title: 'text-blue-800',
    message: 'text-blue-600',
  },
}

export function Notification({ id, type, title, message, duration = 4000, onClose }: NotificationProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [progress, setProgress] = useState(100)
  const Icon = icons[type]
  const colorScheme = colors[type]

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 10)
    
    // Progress bar animation
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - (100 / (duration / 50))
        if (newProgress <= 0) {
          clearInterval(progressTimer)
          handleClose()
          return 0
        }
        return newProgress
      })
    }, 50)

    return () => {
      clearTimeout(timer)
      clearInterval(progressTimer)
    }
  }, [duration])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => onClose(id), 300)
  }

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
        max-w-sm w-full bg-white rounded-lg shadow-lg border ${colorScheme.border}
        overflow-hidden
      `}
      style={{ zIndex: 10000 }}
    >
      {/* Progress Bar */}
      <div className="h-1 bg-gray-200">
        <div
          className={`h-full transition-all duration-75 ease-linear ${
            type === 'success' ? 'bg-green-500' :
            type === 'error' ? 'bg-red-500' :
            type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={`w-5 h-5 ${colorScheme.icon}`} />
          </div>
          
          <div className="ml-3 flex-1">
            <h4 className={`text-sm font-semibold ${colorScheme.title}`}>
              {title}
            </h4>
            {message && (
              <p className={`mt-1 text-sm font-normal ${colorScheme.message}`}>
                {message}
              </p>
            )}
          </div>

          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleClose}
              className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 rounded-md"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Loading notification component
export function LoadingNotification({ id, title, message, onClose }: Omit<NotificationProps, 'type'>) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => onClose(id), 300)
  }

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
        max-w-sm w-full bg-white rounded-lg shadow-lg border border-blue-200
        overflow-hidden
      `}
      style={{ zIndex: 10000 }}
    >
      {/* Loading Bar */}
      <div className="h-1 bg-gray-200">
        <div className="h-full bg-blue-500 animate-pulse" />
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
          </div>
          
          <div className="ml-3 flex-1">
            <h4 className="text-sm font-semibold text-blue-800">
              {title}
            </h4>
            {message && (
              <p className="mt-1 text-sm font-normal text-blue-600">
                {message}
              </p>
            )}
          </div>

          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleClose}
              className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 rounded-md"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
